import { useApi } from '@/shared/api/http';
import {
  ASSISTANT_ANALYZE_ERROR,
  ASSISTANT_CREATE_LEAD_ERROR,
  ASSISTANT_UPDATE_LEAD_ERROR,
  ASSISTANT_WIDGET_LINK_ERROR,
} from '@/entities/assistant/lib/assistantMessages';
import type {
  IAssistantAnalyzeResponse,
  IAssistantLead,
  IAssistantLeadResponse,
  IAssistantWidgetLink,
} from '@/entities/assistant/model/types/assistant';

const hashKeyOptions = (bKeyToken: string | null) =>
  bKeyToken ? { headers: { 'X-HASH-KEY': bKeyToken } } : undefined;

export const useAssistantService = () => {
  const api = useApi();

  const askAssistant = async (text: string, bKeyToken: string | null = null) => {
    try {
      return await api.post<IAssistantAnalyzeResponse>(
        'assistant/analyze',
        { text },
        hashKeyOptions(bKeyToken),
      );
    } catch (error) {
      console.error(ASSISTANT_ANALYZE_ERROR, error);
      throw error;
    }
  };

  const createLead = async (lead: IAssistantLead, bKeyToken: string | null = null) => {
    try {
      return await api.post<IAssistantLeadResponse>(
        'assistant',
        lead,
        hashKeyOptions(bKeyToken),
      );
    } catch (error) {
      console.error(ASSISTANT_CREATE_LEAD_ERROR, error);
      throw error;
    }
  };

  const updateLead = async (lead: IAssistantLead, bKeyToken: string | null = null) => {
    try {
      return await api.patch<IAssistantLeadResponse>(
        'assistant',
        lead,
        hashKeyOptions(bKeyToken),
      );
    } catch (error) {
      console.error(ASSISTANT_UPDATE_LEAD_ERROR, error);
      throw error;
    }
  };

  const resolveWidgetToken = async (token: string) => {
    try {
      return await api.get<IAssistantWidgetLink>(
        `assistant/link/${encodeURIComponent(token)}`,
      );
    } catch (error) {
      console.error(ASSISTANT_WIDGET_LINK_ERROR, error);
      throw error;
    }
  };

  return {
    askAssistant,
    createLead,
    updateLead,
    resolveWidgetToken,
  };
};
